import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * A component that renders the security deposit as a line item (hold, not charged).
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing - The listing object
 * @param {Object} props.transaction - The transaction object (optional)
 * @param {'customer' | 'provider'} props.userRole - The user role
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element|null}
 */
const LineItemDepositMaybe = props => {
  const { listing, transaction, userRole, intl } = props;

  // Get deposit amount from either listing or transaction
  const depositFromListing = listing?.attributes?.publicData?.depositAmount;
  const depositFromTransaction = transaction?.attributes?.protectedData?.depositAmount;
  const depositAmount = depositFromTransaction || depositFromListing;

  if (!depositAmount || !depositAmount.amount) {
    return null;
  }

  const depositMoney = new Money(depositAmount.amount, depositAmount.currency);
  const formattedDeposit = formatMoney(intl, depositMoney);
  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';

  const txTransitions = transaction?.attributes?.transitions || [];
  const depositReleasedTransitions = [
    'transition/refund-deposit',
    'transition/operator-refund-deposit',
    'transition/complete-auto-release',
  ];
  const depositReleasePendingTransitions = [
    'transition/auto-release-deposit',
    'transition/operator-test-auto-release',
  ];

  const hasDepositReleasedTransition = txTransitions.some(t =>
    depositReleasedTransitions.includes(t.transition)
  );
  const hasDepositReleasePendingTransition = txTransitions.some(t =>
    depositReleasePendingTransitions.includes(t.transition)
  );

  const depositPaymentStatus = transaction?.attributes?.protectedData?.depositPaymentDetails?.status;
  // Stripe terminal statuses for deposit PaymentIntent mean hold lifecycle is finished.
  const hasTerminalDepositPaymentStatus = ['canceled', 'succeeded'].includes(depositPaymentStatus);

  const isDepositReleased =
    hasDepositReleasedTransition || depositPaymentStatus === 'canceled';
  const isDepositReleasePending = hasDepositReleasePendingTransition && !isDepositReleased;

  const showCustomerDepositWarning =
    isCustomer && !isDepositReleased && !isDepositReleasePending && !hasTerminalDepositPaymentStatus;

  const depositLabelId = isDepositReleased
    ? 'OrderBreakdown.depositReleased'
    : isDepositReleasePending
    ? 'OrderBreakdown.depositReleasePending'
    : 'OrderBreakdown.deposit';

  const depositNoteId = isProvider
    ? isDepositReleased
      ? 'OrderBreakdown.depositReleasedNoteProvider'
      : isDepositReleasePending
      ? 'OrderBreakdown.depositReleasePendingNoteProvider'
      : 'OrderBreakdown.depositHoldNoteProvider'
    : isDepositReleased
    ? 'OrderBreakdown.depositReleasedNote'
    : isDepositReleasePending
    ? 'OrderBreakdown.depositReleasePendingNote'
    : 'OrderBreakdown.depositHoldNote';

  return (
    <div className={css.lineItemDeposit}>
      <hr className={css.totalDivider} />
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id={depositLabelId} />
        </span>
        <span className={css.itemValue}>
          {formattedDeposit}
          <span className={css.depositNote}>
            <FormattedMessage id={depositNoteId} />
          </span>
        </span>
      </div>
      {showCustomerDepositWarning ? (
        <div className={css.depositWarning}>
          <FormattedMessage id="OrderBreakdown.depositWarningCustomer" />
        </div>
      ) : null}
    </div>
  );
};

export default LineItemDepositMaybe;
